function getPathValue(source, path) {
  if (!source || !path || !/^[\w$-]+(\.[\w$-]+)*$/.test(path)) {
    return undefined;
  }

  return path.split(".").reduce((current, key) => {
    if (current && Object.prototype.hasOwnProperty.call(current, key)) {
      return current[key];
    }

    return undefined;
  }, source);
}

function isTranslatableMessageValue(value) {
  return typeof value === "string" || typeof value === "function";
}

function getMessageValue(messages, key) {
  if (!messages || !key) return undefined;

  if (Object.prototype.hasOwnProperty.call(messages, key)) {
    return messages[key];
  }

  return getPathValue(messages, key);
}

function stripDiacritics(value) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function normalizeKeyName(value) {
  return stripDiacritics(String(value))
    .replace(/[^A-Za-z0-9$]+/g, "")
    .toLowerCase();
}

function normalizeMessageValue(value) {
  return stripDiacritics(String(value))
    .replace(/[^\w$]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function uniq(values) {
  return [...new Set(values.filter(Boolean))];
}

function upperFirst(value) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function splitKeyWords(value) {
  return (
    stripDiacritics(value)
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .match(/[A-Za-z0-9]+/g) || []
  );
}

function toPascalCase(value, removeConnectors = false) {
  const connectors = new Set([
    "a",
    "as",
    "da",
    "das",
    "de",
    "do",
    "dos",
    "e",
    "em",
    "o",
    "os",
    "para",
    "por",
    "sobre",
  ]);

  return splitKeyWords(value)
    .filter((word) => !removeConnectors || !connectors.has(word.toLowerCase()))
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
}

function getKeyCandidates(key) {
  const trimmedKey = key.trim();
  const keyWithoutDiacritics = stripDiacritics(trimmedKey);
  const keyWithUnderscores = keyWithoutDiacritics
    .replace(/[^A-Za-z0-9$]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return uniq([
    trimmedKey,
    keyWithoutDiacritics,
    upperFirst(keyWithoutDiacritics),
    keyWithUnderscores,
    upperFirst(keyWithUnderscores),
    toPascalCase(trimmedKey),
    toPascalCase(trimmedKey, true),
  ]);
}

function getFallbackLocales(fallbackLocale) {
  if (Array.isArray(fallbackLocale)) return fallbackLocale;
  if (fallbackLocale && typeof fallbackLocale === "object") {
    return Object.values(fallbackLocale).flat().filter(Boolean);
  }
  return fallbackLocale ? [fallbackLocale] : [];
}

// No vue-i18n 8, getLocaleMessage(), messages e availableLocales devolvem
// JSON.parse(JSON.stringify()) do dicionário inteiro (~700 KB por idioma) e, dentro
// de um render/computed, registram dependência em cada chave. Lê o objeto interno.
function getAllMessages(i18n) {
  return typeof i18n._getMessages === "function" ? i18n._getMessages() : null;
}

function getLocaleMessages(i18n, locale) {
  const allMessages = getAllMessages(i18n);
  if (allMessages) return allMessages[locale] || {};
  return i18n.getLocaleMessage(locale);
}

function getAvailableLocales(i18n) {
  const allMessages = getAllMessages(i18n);
  if (allMessages) return Object.keys(allMessages).sort();
  return i18n.availableLocales || [];
}

function getLookupLocales(i18n) {
  return uniq([
    i18n.locale,
    ...getFallbackLocales(i18n.fallbackLocale),
    ...getAvailableLocales(i18n),
  ]);
}

function flattenMessageKeys(messages, prefix = "") {
  return Object.entries(messages || {}).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    if (isTranslatableMessageValue(value)) {
      return [{ key: path, value }];
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return flattenMessageKeys(value, path);
    }

    return [];
  });
}

// Índices das buscas por chave/valor normalizados, montados uma vez por dicionário.
// O primeiro registro de cada forma normalizada vence, como no find() sobre a lista.
const messageIndexes = new WeakMap();

function buildIndex(messages, normalize, field) {
  const index = new Map();
  flattenMessageKeys(messages).forEach((message) => {
    const normalized = normalize(message[field]);
    if (normalized && !index.has(normalized)) {
      index.set(normalized, message);
    }
  });
  return index;
}

function getMessageIndex(messages, type) {
  if (!messages || typeof messages !== "object") return new Map();

  let indexes = messageIndexes.get(messages);
  if (!indexes) {
    indexes = {};
    messageIndexes.set(messages, indexes);
  }
  if (!indexes[type]) {
    indexes[type] =
      type === "key"
        ? buildIndex(messages, normalizeKeyName, "key")
        : buildIndex(messages, normalizeMessageValue, "value");
  }
  return indexes[type];
}

function findFirst(values, finder) {
  for (const value of values) {
    const result = finder(value);
    if (result) return result;
  }
  return null;
}

function findMessageByKey(i18n, key, locales) {
  for (const locale of locales) {
    const messages = getLocaleMessages(i18n, locale);
    const messageValue = getMessageValue(messages, key);

    if (isTranslatableMessageValue(messageValue)) {
      return { key, locale, value: messageValue };
    }
  }

  return null;
}

function findMessageByNormalizedKey(i18n, key, locales) {
  const normalizedKey = normalizeKeyName(key);
  if (!normalizedKey) return null;

  for (const locale of locales) {
    const messages = getLocaleMessages(i18n, locale);
    const match = getMessageIndex(messages, "key").get(normalizedKey);

    if (match) {
      return { ...match, locale };
    }
  }

  return null;
}

function findMessageByTranslatedValue(i18n, value, locales) {
  const normalizedValue = normalizeMessageValue(value);
  if (!normalizedValue) return null;

  for (const locale of locales) {
    const messages = getLocaleMessages(i18n, locale);
    const match = getMessageIndex(messages, "value").get(normalizedValue);

    if (match) {
      return { ...match, locale };
    }
  }

  return null;
}

function translateResolvedMessage(i18n, message, preferredLocales) {
  const currentLocaleMessage = findMessageByKey(
    i18n,
    message.key,
    preferredLocales
  );
  if (currentLocaleMessage) return i18n.t(message.key);

  if (typeof message.value === "function") {
    return i18n.t(message.key, message.locale);
  }

  return message.value;
}

export function translateIfMessageExists(i18n, value) {
  if (value === null || value === undefined) return "";

  const key = String(value);
  if (!i18n || !key) return key;

  const lookupLocales = getLookupLocales(i18n);
  const preferredLocales = uniq([
    i18n.locale,
    ...getFallbackLocales(i18n.fallbackLocale),
  ]);

  const candidates = getKeyCandidates(key);
  const message =
    findFirst(candidates, (candidate) =>
      findMessageByKey(i18n, candidate, lookupLocales)
    ) ||
    findFirst(candidates, (candidate) =>
      findMessageByNormalizedKey(i18n, candidate, lookupLocales)
    ) ||
    findMessageByTranslatedValue(i18n, key, lookupLocales);

  return message
    ? translateResolvedMessage(i18n, message, preferredLocales)
    : key;
}
